/**
 * Recording Button Component
 * Reusable button for starting/stopping conversation recording
 * Shows appropriate icon and text based on recording state
 */

import React from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import microphoneTokens from '@amzn/meridian-tokens/base/icon/microphone';
import stopTokens from '@amzn/meridian-tokens/base/icon/stop';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  startRecording,
  stopRecording,
  recordingStopped,
  audioStreamAcquired,
  audioStreamReleased,
  disableNoteRecording,
} from '../../../store/recordingSlice';
import { fetchSession } from '../../../store/sessionSlice';
import { fetchTranscript } from '../../../store/transcriptSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { audioService } from '../../../services/audioService';
import { websocketService } from '../../../services/websocketService';
import { LANGUAGE_OPTIONS_BILINGUAL_EN_ES } from '../../../constants';
import { TEXT_COLOR_SECONDARY } from '../cssConstants';
import { v4 as uuidv4 } from 'uuid';

interface RecordingButtonProps {
  hasContent?: boolean;
  size?: 'small' | 'medium' | 'large';
  type?: 'primary' | 'secondary' | 'tertiary';
  showDisabledHelper?: boolean;
  useCaseType?: string;
  bilingualMode?: boolean;
  siteCode?: string;
  disabled?: boolean;
  // Optional label override for the start action (e.g. on the resume flow).
  startLabel?: string;
  // Fired right before recording actually starts (after mic permission).
  onBeforeStart?: () => void;
}

const RecordingButton: React.FC<RecordingButtonProps> = ({
  hasContent = false,
  size = 'medium',
  type = 'tertiary',
  showDisabledHelper = false,
  useCaseType,
  bilingualMode = false,
  siteCode,
  disabled = false,
  startLabel,
  onBeforeStart,
}) => {
  const dispatch = useAppDispatch();

  const { currentSession } = useAppSelector((state) => state.session);
  const {
    isRecording,
    isNoteRecording,
    isFileStreaming,
  } = useAppSelector((state) => state.recording);

  // Hide entirely when file streaming is active (user uses Stop/Continue from AudioFileUpload)
  if (isFileStreaming) return null;


  // Disable conversation recording when note recording is active or externally disabled
  const canRecordConversation = !isNoteRecording && !disabled;

  const handleStartRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        dispatch(showError('Your browser does not support audio capture'));
        return;
      }

      const stream = await audioService.requestMicrophoneAccess();
      dispatch(audioStreamAcquired());

      websocketService.initialize(dispatch);

      const connectionId = `conn-${uuidv4()}`;
      const existingSessionId = currentSession?.session_id;
      await websocketService.connect(connectionId, existingSessionId);

      const audioFormat = audioService.getAudioFormat();
      const sampleRate = audioService.getSampleRate();

      onBeforeStart?.();

      // Pass bilingual language code: use session's existing language_code when resuming,
      // or the bilingual toggle for new sessions. This ensures resumed recordings in a
      // bilingual session continue using multilingual mode with translations.
      const languageCode = currentSession?.language_code
        || (bilingualMode ? LANGUAGE_OPTIONS_BILINGUAL_EN_ES : undefined);
      websocketService.startRecording(existingSessionId, audioFormat, sampleRate, useCaseType, languageCode, siteCode);

      // THEN start audio capture (audio chunks will arrive after backend has the message)
      await audioService.startRecording(stream);

      dispatch(startRecording({ connectionId }));

      dispatch(showSuccess('Conversation started'));
      dispatch(disableNoteRecording());
    } catch (error: any) {
      console.error('Failed to start recording:', error);
      dispatch(showError(error.message || 'Failed to start conversation'));
      
      audioService.releaseMicrophone();
      dispatch(audioStreamReleased());
    }
  };

  const handleStopRecording = async () => {
    try {
      // Capture session ID before stop() clears it
      const sessionId = websocketService.getSessionId() || currentSession?.session_id;
      console.log('[DEBUG] handleStopRecording called, wsConnected:', websocketService.isConnected(), 'sessionId:', sessionId);
      
      await audioService.stopRecording();
      websocketService.stop();
      console.log('[DEBUG] stop_session sent to backend');
      
      dispatch(stopRecording());
      dispatch(recordingStopped());
      
      audioService.releaseMicrophone();
      dispatch(audioStreamReleased());

      // Safety net: fetch fresh session + transcript data from backend.
      // The primary path is via the WebSocket recording_status:paused message,
      // but if that message is missed (e.g., WebSocket closed before delivery),
      // this ensures persisted transcript segments replace live grey ones.
      if (sessionId) {
        dispatch(fetchSession(sessionId) as any);
        dispatch(fetchTranscript(sessionId) as any);
      }

      dispatch(showSuccess('Conversation stopped'));
    } catch (error: any) {
      console.error('Failed to stop recording:', error);
      dispatch(showError('Failed to stop conversation'));
    }
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      await handleStopRecording();
    } else {
      await handleStartRecording();
    }
  };

  return (
    <>
      <Button
        onClick={handleToggleRecording}
        size={size}
        type={type}
        disabled={!canRecordConversation}
      >
        {isRecording ? (
          <>
            <Icon tokens={stopTokens} /> Stop
          </>
        ) : (
          <>
            <Icon tokens={microphoneTokens} /> {startLabel ?? (hasContent ? 'Add More Conversation' : 'Start Session')}
          </>
        )}
      </Button>

      {/* Helper text when disabled */}
      {showDisabledHelper && !canRecordConversation && (
        <span style={{ fontSize: '11px', color: TEXT_COLOR_SECONDARY }}>
          Disabled during voice note
        </span>
      )}
    </>
  );
};

export default RecordingButton;
